const LOOKUP_URL = '/api/lookup.php';

export interface MiniappFeatureFlags {
  ok: boolean;
  new_home: boolean;
  report_problem: boolean;
  find_service: boolean;
  find_dealer?: boolean;
  find_spare_part?: boolean;
  our_contacts?: boolean;
  feature_copy?: Partial<Record<'report_problem' | 'find_service' | 'find_dealer' | 'find_spare_part' | 'our_contacts', {
    title?: string;
    description?: string;
  }>>;
  external_access_token?: string | null;
  identity_source?: 'init_data' | 'access_token' | 'claimed_user_id' | 'anonymous';
  error?: { code: string; message?: string };
}

/** Серверная доступность preview и разделов мини-аппа. */
export async function fetchMiniappFeatureFlags(
  initData: string,
  accessToken = '',
  userId?: number,
): Promise<MiniappFeatureFlags> {
  const res = await fetch('/api/miniapp_access_v3.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      initData,
      ...(accessToken ? { accessToken } : {}),
      ...(userId !== undefined ? { userId } : {}),
    }),
  });
  const body = (await res.json()) as MiniappFeatureFlags;
  if (!res.ok || !body.ok) {
    throw new Error(body.error?.message ?? 'Не удалось загрузить настройки мини-приложения.');
  }
  return body;
}

export interface ServiceStation {
  id: number;
  organization: string;
  address: string;
  site: string | null;
  phones: string[];
  emails: string[];
  types: string[];
  latitude: number;
  longitude: number;
}

export interface ContactCard {
  id: number;
  title: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  phone: string;
}

export async function fetchContactCards(): Promise<ContactCard[]> {
  const res = await fetch('/api/contact_cards.php', { method: 'GET' });
  const body = (await res.json()) as { ok: boolean; items?: ContactCard[]; error?: { message?: string } };
  if (!res.ok || !body.ok) {
    throw new Error(body.error?.message ?? 'Не удалось загрузить контакты.');
  }
  return body.items ?? [];
}

export interface ServiceStationsResult {
  ok: boolean;
  items: ServiceStation[];
  updated_at: string | null;
  error?: { code: string; message: string };
}

/** Публичный облегчённый список активных СТО для поиска на устройстве. */
export async function fetchServiceStations(): Promise<ServiceStationsResult> {
  const res = await fetch('/api/service_stations.php', { method: 'GET' });
  const body = (await res.json()) as ServiceStationsResult;
  if (!res.ok || !body.ok) {
    throw new Error(body.error?.message ?? 'Не удалось загрузить список СТО.');
  }
  return body;
}

export interface Dealer {
  id: string;
  remote_id: number;
  city_id: number;
  city: string;
  region: string;
  organization: string;
  address: string;
  phones: string[];
  emails: string[];
  sites: string[];
}

export async function fetchDealers(): Promise<{ ok: boolean; items: Dealer[]; updated_at: string | null }> {
  const res = await fetch('/api/dealers.php', { method: 'GET' });
  const body = await res.json();
  if (!res.ok || !body.ok || !Array.isArray(body.items)) {
    throw new Error(body.error?.message ?? 'Не удалось загрузить список дилеров.');
  }
  return body;
}

export interface VinManager {
  bitrix_id: number;
  name?: string | null;
}

export type VinData = string | Record<string, unknown>;

export interface VinSuccess {
  ok: true;
  vin: string;
  manager: VinManager | null;
  /** Bitrix DIALOG_ID чата, например "chat12535". Присутствует только в ответе lookup. */
  chat?: string | null;
  /** Код контрагента 1С — используется сервером для маршрутизации в чат по таблице «Контрагенты». */
  counterparty?: string | null;
  article?: string | null;
  name?: string | null;
  data: VinData | null;
  /** true — уведомление отправлено; false — отправка не удалась; null — ещё не отправлялось. */
  notified: boolean | null;
  /** 'bitrix' — ушло в Bitrix; 'none' — отправка была, но получателя нет; null — ещё не отправляли (lookup). */
  notified_via: 'bitrix' | 'none' | null;
  /** Детали ошибки доставки в Bitrix, если notified === false. */
  notify_error?: { code: string; message: string } | null;
}

export interface VinFailure {
  ok: false;
  error: { code: string; message: string };
  /** Данные из 1С для отправки в Bitrix при подтверждении VIN пользователем. */
  fallback?: unknown;
}

export type VinResult = VinSuccess | VinFailure;

export async function confirmVin(
  vin: string,
  initData: string,
  fallback: unknown,
  phone?: string | null,
  comment?: string | null,
  repeat?: boolean,
): Promise<VinResult> {
  const res = await fetch('/api/confirm.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      vin, initData, fallback,
      ...(phone ? { phone } : {}),
      ...(comment ? { comment } : {}),
      ...(repeat ? { repeat: true } : {}),
    }),
  });
  return (await res.json()) as VinResult;
}

export interface ProfileResult {
  ok: boolean;
  phone: string | null;
  error?: {
    code?: string;
    message?: string;
  };
}

export interface HistoryItem {
  id: number;
  /** Человекочитаемый номер обращения, например 123. */
  request_no?: number | null;
  /** request_id (16 hex) — для отправки обратной связи. */
  request_id: string;
  vin: string;
  created_at: string;
  comment: string | null;
  /** 'new' — отправлен; 'in_progress' — взят в работу; 'done' — обработан. */
  status: string;
  /** Ответ клиента на «Ваша проблема решена?» — 'resolved'/'unresolved'/null. */
  feedback: string | null;
}

export interface HistoryResult {
  ok: boolean;
  items: HistoryItem[];
}

export interface UserVinItem {
  vin: string;
  article: string | null;
  name: string | null;
  model: string | null;
  last_used_at: string;
}

export interface UserVinsResult {
  ok: boolean;
  items: UserVinItem[];
  error?: { code?: string; message?: string };
}

/** VIN, которые текущий пользователь указывал в прежних обращениях. */
export async function fetchUserVins(initData: string): Promise<UserVinsResult> {
  const res = await fetch('/api/user_vins.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData }),
  });
  return (await res.json()) as UserVinsResult;
}

/** История обращений текущего пользователя (по max_user_id из initData). */
export async function fetchHistory(initData: string): Promise<HistoryResult> {
  const res = await fetch('/api/history.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData }),
  });
  return (await res.json()) as HistoryResult;
}

/** Отправляет ответ клиента на вопрос «Ваша проблема решена?». */
export async function sendFeedback(initData: string, requestId: string, answer: 'resolved' | 'unresolved'): Promise<{ ok: boolean; recorded: boolean }> {
  const res = await fetch('/api/feedback.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData, request_id: requestId, answer }),
  });
  return (await res.json()) as { ok: boolean; recorded: boolean };
}

/** Скрывает запись из истории пользователя (мягкое удаление). Возвращает обновлённый список. */
export async function deleteHistory(initData: string, id: number): Promise<HistoryResult> {
  const res = await fetch('/api/history.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData, delete_id: id }),
  });
  return (await res.json()) as HistoryResult;
}

/** Запрашивает сохранённый телефон пользователя по initData (узнаём по max_user_id). */
export async function fetchProfile(initData: string): Promise<ProfileResult> {
  const res = await fetch('/api/profile.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData }),
  });
  return (await res.json()) as ProfileResult;
}

/** Сохраняет телефон пользователя (после первого requestContact). */
export async function saveProfile(initData: string, phone: string): Promise<ProfileResult> {
  const res = await fetch('/api/profile.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData, phone }),
  });
  return (await res.json()) as ProfileResult;
}

export interface MiniappRestartResult {
  ok: boolean;
  url?: string;
  error?: { code?: string; message?: string };
}

/** Returns a MAX deep link that launches the mini-app with fresh initData. */
export async function fetchMiniappRestartUrl(): Promise<MiniappRestartResult> {
  const res = await fetch('/api/miniapp_restart.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: '{}',
  });
  return (await res.json()) as MiniappRestartResult;
}

export async function lookupVin(
  vin: string,
  initData: string,
): Promise<VinResult> {
  const res = await fetch(LOOKUP_URL, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ vin, initData }),
  });
  return (await res.json()) as VinResult;
}

export interface SparePartResult {
  ok: boolean;
  request_id?: string;
  error?: { code: string; message: string; field?: 'region' | 'part_name' | 'email' | 'vin' | 'photos' | null };
}

export interface SparePartHistoryItem {
  id: number;
  created_at: string;
  region: string;
  part_name: string;
  vin: string | null;
  notified: boolean;
  article: string | null;
  name: string | null;
}

export interface SparePartHistoryResult {
  ok: boolean;
  items: SparePartHistoryItem[];
  has_more: boolean;
  next_cursor: number | null;
  error?: { code?: string; message?: string };
}

export async function fetchSparePartHistory(initData: string, beforeId?: number): Promise<SparePartHistoryResult> {
  const response = await fetch('/api/spare_part_history.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData, ...(beforeId ? { before_id: beforeId } : {}) }),
  });
  return (await response.json()) as SparePartHistoryResult;
}

export async function submitSparePartRequest(input: {
  initData: string; region: string; partName: string; email: string; vin?: string; photos?: File[];
}): Promise<SparePartResult> {
  const body = new FormData();
  body.set('initData', input.initData); body.set('region', input.region); body.set('part_name', input.partName);
  body.set('email', input.email); body.set('vin', input.vin ?? '');
  for (const photo of input.photos ?? []) body.append('photos[]', photo, photo.name);
  let response: Response;
  try {
    response = await fetch('/api/spare_part_request.php', { method: 'POST', body });
  } catch {
    throw new Error(input.photos?.length
      ? 'Не удалось загрузить фотографии. Проверьте соединение и повторите отправку.'
      : 'Не удалось связаться с сервером. Проверьте соединение и повторите отправку.');
  }
  let result: SparePartResult;
  try { result = await response.json() as SparePartResult; }
  catch { throw new Error(response.status === 413 ? 'Общий размер фотографий слишком большой.' : 'Некорректный ответ сервера.'); }
  if (!response.ok && !result.error) throw new Error('Не удалось отправить заявку.');
  return result;
}
